import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useParams } from "react-router-dom";
import { ShieldCheck, Award, Headphones, Lock, UserCheck } from "lucide-react";
import RegistrationForm from "../components/RegistrationForm";
import { createPartner, resolveMaster } from "../api";
import { toast } from "../hooks/use-toast";

const BB_LOGO = "https://customer-assets-m6fa6gv7.emergentagent.net/job_parceiros-bb-digital/artifacts/jxz0vlm8_icon-logo-bb-100.png";

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const { slug } = useParams();
  const masterCode = slug || searchParams.get("m") || searchParams.get("p") || "";
  const [master, setMaster] = useState(null);

  useEffect(() => {
    let active = true;
    if (masterCode) {
      resolveMaster(masterCode).then((m) => { if (active) setMaster(m); });
    } else {
      setMaster(null);
    }
    return () => { active = false; };
  }, [masterCode]);

  const handleSubmit = async (data) => {
    await createPartner({
      ...data,
      masterCode: master ? master.code : (masterCode || ""),
      masterName: master ? master.name : "",
    });
    toast({ title: "Cadastro realizado!", description: "Seus dados foram salvos com sucesso." });
  };

  return (
    <div className="min-h-screen bb-grid-bg">
      {/* Hero (blue) */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0A1B6B] via-[#465EFF] to-[#0A1B6B]">
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-[#FCFC30]/20 blur-3xl" />
        {/* BB logo watermark (user-provided asset) */}
        <img
          src={BB_LOGO}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute -right-6 top-1/2 hidden w-72 -translate-y-1/2 opacity-[0.10] brightness-0 invert sm:block"
        />
        {/* Admin access link */}
        <div className="mx-auto flex max-w-6xl justify-end px-5 pt-5">
          <Link
            to="/admin"
            className="flex items-center gap-1.5 text-sm font-medium text-blue-100 transition-colors hover:text-[#FCFC30]"
          >
            <Lock className="h-3.5 w-3.5" /> Área Administrativa
          </Link>
        </div>
        <div className="relative mx-auto max-w-6xl px-5 pb-20 pt-8 sm:pb-28 sm:pt-12">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#FCFC30] px-3 py-1 text-xs font-bold uppercase tracking-wide text-[#0A1B6B]">
              <Award className="h-3.5 w-3.5" /> Representante Autorizado
            </span>
            {master && (
              <div className="mt-4 inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm font-medium text-white backdrop-blur-sm">
                <UserCheck className="h-4 w-4 text-[#FCFC30]" />
                Cadastro vinculado a: <span className="font-bold text-[#FCFC30]">{master.name}</span>
              </div>
            )}
            <h1 className="mt-5 font-heading text-3xl font-extrabold leading-tight text-white sm:text-5xl">
              Seja um parceiro <span className="text-[#FCFC30]">BB Consórcios</span>
            </h1>
            <p className="mt-4 max-w-xl text-base text-blue-100 sm:text-lg">
              Cadastre-se em nossa rede de representantes autorizados e faça parte de um dos
              maiores programas de consórcios do Brasil. Preencha o formulário e comece agora.
            </p>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3">
              {[
                { icon: ShieldCheck, text: "Marca reconhecida e confiável" },
                { icon: Award, text: "Comissões atrativas" },
                { icon: Headphones, text: "Suporte dedicado" },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2 text-sm font-medium text-blue-50">
                  <Icon className="h-4 w-4 text-[#FCFC30]" /> {text}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Form card */}
      <main className="relative mx-auto mt-12 max-w-3xl px-5 pb-20">
        <img
          src={BB_LOGO}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-24 w-[420px] max-w-[80%] -translate-x-1/2 opacity-[0.04]"
        />
        <div className="relative rounded-2xl border border-slate-100 bg-white/95 p-6 shadow-xl shadow-[#0A1B6B]/5 backdrop-blur-sm sm:p-9">
          <div className="mb-6">
            <h3 className="font-heading text-lg font-bold text-[#0A1B6B]">Formulário de Cadastro</h3>
            <p className="text-sm text-slate-400">Leva apenas alguns minutos</p>
          </div>
          <RegistrationForm onSubmit={handleSubmit} />
        </div>
      </main>

      <footer className="border-t border-slate-100 bg-white py-8">
        <div className="mx-auto max-w-6xl px-5 text-center text-xs leading-relaxed text-slate-400">
          <p className="font-medium text-slate-500">BB Consórcios — Representante Autorizado</p>
          <p className="mt-1">
            Este site é operado por um representante autorizado. Os dados fornecidos são tratados
            conforme a Lei Geral de Proteção de Dados (LGPD).
          </p>
        </div>
      </footer>
    </div>
  );
}
